import getRetailTokenClient from "@utils/getRetailToken.client";
import { apiClient } from "./apiClient";
import { fetchValuationRequest } from "@services/valuationRequest";
import {
  REQUEST_CALLBACK_URL,
  VALUATION_REQUEST_CALLBACK_V2_URL,
} from "./endpoints";

export interface RequestCallbackPayload {
  callback_message: string;
}

export interface ValuationRequestCallbackPayload {
  acko_variant_id: number;
  callback_message: string;
}

export interface RequestCallbackApiResponse {
  status: string;
}

export interface RequestCallbackResponse {
  success: boolean;
  data?: RequestCallbackApiResponse;
  error?: {
    message: string;
  };
}

/**
 * Post request callback data to the callback API
 */
export const postRequestCallback = async (
  payload: RequestCallbackPayload
): Promise<RequestCallbackResponse> => {
  try {
    const retailToken = await getRetailTokenClient();
    
    if (!retailToken) {
      return {
        success: false,
        error: {
          message: "No retail token available",
        },
      };
    }

    // Make API request
    const response = await apiClient.post(
      REQUEST_CALLBACK_URL,
      payload,
      {
        headers: {
          "retailtoken": retailToken,
          "Content-Type": "application/json",
        },
      }
    );

    return {
      success: true,
      data: response,
    };
  } catch (error: any) {
    console.error("Request callback API error:", error.message);
    return {
      success: false,
      error: {
        message: error instanceof Error ? error.message : "Unknown error",
      },
    };
  }
};

/**
 * Car valuation v2 request-callback (requires acko_variant_id + retailtoken).
 */
export const postValuationRequestCallback = async (
  payload: ValuationRequestCallbackPayload
): Promise<RequestCallbackResponse> => {
  try {
    const retailToken = await getRetailTokenClient();

    if (!retailToken) {
      return {
        success: false,
        error: {
          message: "No retail token available",
        },
      };
    }

    if (
      payload.acko_variant_id == null ||
      Number.isNaN(Number(payload.acko_variant_id))
    ) {
      return {
        success: false,
        error: {
          message: "acko_variant_id is required",
        },
      };
    }

    const response = await fetchValuationRequest(
      VALUATION_REQUEST_CALLBACK_V2_URL,
      {
        method: "POST",
        headers: {
          retailtoken: retailToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          acko_variant_id: Number(payload.acko_variant_id),
          callback_message: payload.callback_message,
        }),
      }
    );
    const responseData = await response.json();

    if (!response.ok) {
      throw new Error(responseData?.message || "Valuation callback request failed");
    }

    return {
      success: true,
      data: responseData,
    };
  } catch (error: unknown) {
    const message =
      error instanceof Error ? error.message : "Unknown error";
    console.error("Valuation request callback API error:", message);
    return {
      success: false,
      error: { message },
    };
  }
};
